import * as Sentry from '@sentry/tanstackstart-react'
import mixpanel from 'mixpanel-browser'
import { ANALYTICS_SOURCES, EMITTERS, ORIGIN_PRODUCTS } from './analytics-contracts'

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN
const MIXPANEL_ENVIRONMENT =
  import.meta.env.VITE_ANALYTICS_ENVIRONMENT ||
  import.meta.env.VITE_SENTRY_ENVIRONMENT ||
  import.meta.env.MODE

let initialized = false

const registerSource = () => {
  if (!initialized) return
  mixpanel.register({
    source: ANALYTICS_SOURCES.CLIENT,
    environment: MIXPANEL_ENVIRONMENT,
    origin_product: ORIGIN_PRODUCTS.APP,
    emitter: EMITTERS.BROWSER,
  })
}

const ALIASED_KEY = 'mixpanel_aliased'

const hasBeenAliased = (userId: string): boolean => {
  if (typeof window === 'undefined') return false
  const aliased = localStorage.getItem(ALIASED_KEY)
  return aliased === userId
}

const markAsAliased = (userId: string): void => {
  if (typeof window === 'undefined') return
  localStorage.setItem(ALIASED_KEY, userId)
}

export function initMixpanel(): void {
  if (initialized) return

  if (!MIXPANEL_TOKEN) {
    Sentry.logger.warn('[Mixpanel] Token is missing; analytics disabled', {
      env: MIXPANEL_ENVIRONMENT,
    })
    return
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: import.meta.env.DEV,
    track_pageview: false,
    persistence: 'localStorage',
    autocapture: false,
    record_sessions_percent: 0,
  })

  initialized = true
  registerSource()

  if (import.meta.env.DEV) {
    console.log('%c[Mixpanel] Initialized with manual tracking only', 'color: #7856FF; font-weight: bold')
  }
}

export function isMixpanelInitialized(): boolean {
  return initialized
}

export function aliasAndIdentify(userId: string, properties?: Record<string, unknown>): void {
  aliasAndIdentifyFrom(userId, undefined, properties)
}

export function aliasAndIdentifyFrom(
  userId: string,
  originalDistinctId?: string,
  properties?: Record<string, unknown>
): void {
  if (hasBeenAliased(userId)) {
    identifyUser(userId, properties)
    return
  }

  if (!initialized) {
    if (import.meta.env.DEV) {
      console.warn('[Mixpanel] Alias/Identify NOT called - not initialized')
    }
    return
  }

  if (originalDistinctId && originalDistinctId !== userId) {
    mixpanel.alias(userId, originalDistinctId)
  } else {
    mixpanel.alias(userId)
  }
  markAsAliased(userId)
  mixpanel.identify(userId)

  if (properties) {
    mixpanel.people.set(properties)
  }
}

export function identifyUser(userId: string, properties?: Record<string, unknown>): void {
  if (import.meta.env.DEV) {
    console.log('%c[Mixpanel] identifyUser() called', 'color: #7856FF; font-weight: bold', { userId, properties })
  }

  if (!initialized) {
    if (import.meta.env.DEV) {
      console.warn('[Mixpanel] Identify NOT called - not initialized')
    }
    return
  }

  mixpanel.identify(userId)

  if (properties) {
    mixpanel.people.set(properties)
  }
}

export default mixpanel
